import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { FirebaseAuthService } from './firebase-auth.service';
import { AuthInterceptor } from './auth.interceptor';
import { GrpcWrapperService } from './grpc-wrapper.service';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

/** Provides role-based access control checks by querying project permissions from the backend via gRPC. */
@Injectable({
  providedIn: 'root',
})
export class RbacService {
  private environmentService = inject(EnvironmentService);
  private firebaseAuthService = inject(FirebaseAuthService);
  private grpcWrapper = inject(GrpcWrapperService);

  constructor() {
    // Expose cleanup method on window for development debugging
    if (this.environmentService.isDevelopment()) {
      (window as any).cleanupProjectPermissions = (projectId: string) => this.removeProjectFromCurrentUser(projectId);
    }
  }

  async getUserProjects(): Promise<any[]> {
    try {
      const apiServer = this.environmentService.getApiServer();

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const GetUserProjectsRequest =
        rbacPbModule.GetUserProjectsRequest || (rbacPbModule as any).default?.GetUserProjectsRequest;

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      // Create the request
      const request = new GetUserProjectsRequest();

      ClientLoggingService.log('INFO', `[RbacService] Getting user projects`);

      // Make the gRPC call
      const response = await client.getUserProjects(request, null);
      const projects = response.getProjectsList();

      ClientLoggingService.log('INFO', `[RbacService] Retrieved ${projects.length} projects`);

      // Convert to JavaScript objects
      return projects.map(project => project.toObject());
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error getting user projects:', error);
      throw error;
    }
  }

  async getProjectMembers(projectId: string): Promise<any[]> {
    try {
      const apiServer = this.environmentService.getApiServer();

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const GetProjectMembersRequest =
        rbacPbModule.GetProjectMembersRequest || (rbacPbModule as any).default?.GetProjectMembersRequest;

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      // Create the request
      const request = new GetProjectMembersRequest();
      request.setArchitecturalProjectId(projectId);

      ClientLoggingService.log('INFO', `[RbacService] Getting project members for: ${projectId}`);

      // Make the gRPC call
      const response = await client.getProjectMembers(request, null);
      const members = response.getMembersList();

      ClientLoggingService.log('INFO', `[RbacService] Retrieved ${members.length} members`);

      // Convert to JavaScript objects
      return members.map(member => member.toObject());
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error getting project members:', error);
      throw error;
    }
  }

  /**
   * Invite a user to a project with a specific role
   */
  async inviteUserToProject(projectId: string, email: string, role: number): Promise<any> {
    try {
      const apiServer = this.environmentService.getApiServer();

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const InviteUserToProjectRequest =
        rbacPbModule.InviteUserToProjectRequest || (rbacPbModule as any).default?.InviteUserToProjectRequest;

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      // Create the request
      const request = new InviteUserToProjectRequest();
      request.setArchitecturalProjectId(projectId);
      request.setInviteeEmail(email);
      request.setRole(role);

      ClientLoggingService.log(
        'INFO',
        `[RbacService] Inviting user ${email} to project ${projectId} with role ${role}`
      );

      // Make the gRPC call
      const response = await client.inviteUserToProject(request, null);

      ClientLoggingService.log('INFO', `[RbacService] Invite response:`, response.toObject());

      return response.toObject();
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error inviting user to project:', error);
      throw error;
    }
  }

  /**
   * Update a user's role in a project
   */
  async updateUserRole(projectId: string, userEmail: string, newRole: number): Promise<any> {
    try {
      const apiServer = this.environmentService.getApiServer();

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const UpdateUserRoleRequest =
        rbacPbModule.UpdateUserRoleRequest || (rbacPbModule as any).default?.UpdateUserRoleRequest;

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      // Create the request
      const request = new UpdateUserRoleRequest();
      request.setArchitecturalProjectId(projectId);
      request.setUserEmail(userEmail);
      request.setNewRole(newRole);

      ClientLoggingService.log(
        'INFO',
        `[RbacService] Updating user ${userEmail} role in project ${projectId} to ${newRole}`
      );

      // Make the gRPC call
      const response = await client.updateUserRole(request, null);

      ClientLoggingService.log('INFO', `[RbacService] Update role response:`, response.toObject());

      return response.toObject();
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Remove a deleted project from current user's permissions (clean up Firebase custom claims)
   * This is useful when a project has been deleted from GCS but the user still has permissions in Firebase
   */
  async removeProjectFromCurrentUser(projectId: string): Promise<any> {
    try {
      // Get current user from Firebase auth service
      const user = await lastValueFrom(this.firebaseAuthService.user$.pipe(take(1)));
      if (!user || !user.email) {
        throw new Error('No authenticated user found');
      }

      ClientLoggingService.log(
        'INFO',
        `[RbacService] Removing deleted project ${projectId} from user ${user.email}'s permissions`
      );

      // Set role to NONE (0) to remove the project from user's permissions
      return await this.updateUserRole(projectId, user.email, 0); // UserRole.NONE = 0
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error removing project from user permissions:', error);
      throw error;
    }
  }

  getUserRoleDisplayName(role: number): string {
    switch (role) {
      case 0:
        return 'None';
      case 1:
        return 'Reader';
      case 2:
        return 'Prompter';
      case 3:
        return 'Editor';
      case 4:
        return 'Owner';
      default:
        return 'Unknown';
    }
  }

  /**
   * Check if current user is project owner
   */
  async isCurrentUserProjectOwner(projectId: string): Promise<boolean> {
    try {
      const projects = await this.getUserProjects();
      const project = projects.find(p => p.architecturalProjectId === projectId);
      return project && project.role === 4; // UserRole.OWNER = 4
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error checking project ownership:', error);
      return false;
    }
  }

  /**
   * Remove user from project (owner only)
   */
  async removeUserFromProject(projectId: string, userEmail: string): Promise<any> {
    try {
      ClientLoggingService.log('INFO', `[RbacService] Removing ${userEmail} from project ${projectId}`);

      const apiServer = this.environmentService.getApiServer();
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const RemoveUserFromProjectRequest =
        rbacPbModule.RemoveUserFromProjectRequest || (rbacPbModule as any).default?.RemoveUserFromProjectRequest;
      const { AuthInterceptor } = await import('./auth.interceptor');

      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = { unaryInterceptors: [authInterceptor] };
      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      const request = new RemoveUserFromProjectRequest();
      request.setArchitecturalProjectId(projectId);
      request.setUserEmail(userEmail);

      const response = await client.removeUserFromProject(request, null);

      ClientLoggingService.log('INFO', `[RbacService] Remove user response:`, response.toObject());
      return response.toObject();
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error removing user from project:', error);
      throw error;
    }
  }

  /**
   * Check if a user has admin role
   */
  checkAdminRole(targetEmail: string = '', adminRole: string = ''): Observable<any> {
    return new Observable(observer => {
      this.checkAdminRoleAsync(targetEmail, adminRole)
        .then(response => {
          observer.next(response);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  private async checkAdminRoleAsync(targetEmail: string = '', adminRole: string = ''): Promise<any> {
    try {
      const apiServer = this.environmentService.getApiServer();
      ClientLoggingService.log('INFO', `[RbacService] DEBUG: API Server: ${apiServer}`);

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const CheckAdminRoleRequest =
        rbacPbModule.CheckAdminRoleRequest || (rbacPbModule as any).default?.CheckAdminRoleRequest;
      ClientLoggingService.log(
        'INFO',
        `[RbacService] DEBUG: gRPC imports successful. Client: ${!!RBACServiceClient}, Request: ${!!CheckAdminRoleRequest}`
      );

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);
      ClientLoggingService.log('INFO', `[RbacService] DEBUG: gRPC client created for https://${apiServer}:443`);

      // Create the request
      const request = new CheckAdminRoleRequest();
      if (targetEmail) {
        request.setTargetEmail(targetEmail);
      }
      if (adminRole) {
        request.setAdminRole(adminRole);
      }

      ClientLoggingService.log(
        'INFO',
        `[RbacService] DEBUG: Request created - targetEmail: '${targetEmail}', adminRole: '${adminRole}'`
      );
      ClientLoggingService.log(
        'INFO',
        `[RbacService] Checking admin role for user: ${targetEmail || 'current'}, role: ${adminRole || 'any'}`
      );

      // Make the gRPC call
      ClientLoggingService.log('INFO', `[RbacService] DEBUG: Making gRPC call to checkAdminRole...`);
      const response = await client.checkAdminRole(request, null);
      const result = response.toObject();

      ClientLoggingService.log('INFO', `[RbacService] DEBUG: gRPC call completed successfully`);
      ClientLoggingService.log('INFO', `[RbacService] Admin role check result:`, result);
      return result;
    } catch (error: any) {
      ClientLoggingService.log('ERROR', '[RbacService] ERROR: Admin role check failed:', error);
      ClientLoggingService.log('ERROR', '[RbacService] ERROR: Error message:', error?.message);
      ClientLoggingService.log('ERROR', '[RbacService] ERROR: Error code:', error?.code);
      ClientLoggingService.log('ERROR', '[RbacService] ERROR: Error details:', error?.details);
      throw error;
    }
  }

  /**
   * Get RBAC configuration as YAML (admin only)
   */
  getRbacYaml(): Observable<any> {
    return new Observable(observer => {
      this.getRbacYamlAsync()
        .then(response => {
          observer.next(response);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  private async getRbacYamlAsync(): Promise<any> {
    try {
      const apiServer = this.environmentService.getApiServer();

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const GetRbacYamlRequest = rbacPbModule.GetRbacYamlRequest || (rbacPbModule as any).default?.GetRbacYamlRequest;

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      // Create the request
      const request = new GetRbacYamlRequest();

      ClientLoggingService.log('INFO', `[RbacService] Getting RBAC YAML configuration`);

      // Make the gRPC call
      const response = await client.getRbacYaml(request, null);
      const result = response.toObject();

      ClientLoggingService.log('INFO', `[RbacService] Retrieved RBAC YAML with ${result.userCount} users`);
      return result;
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error getting RBAC YAML:', error);
      throw error;
    }
  }

  /**
   * Set RBAC configuration from YAML (admin only)
   */
  setRbacYaml(yamlContent: string): Observable<any> {
    return new Observable(observer => {
      this.setRbacYamlAsync(yamlContent)
        .then(response => {
          observer.next(response);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  private async setRbacYamlAsync(yamlContent: string): Promise<any> {
    try {
      const apiServer = this.environmentService.getApiServer();

      // Dynamic imports for gRPC client
      const rbacClientModule = await import('../../generated.commonjs/RbacServiceClientPb');
      const RBACServiceClient =
        rbacClientModule.RBACServiceClient || (rbacClientModule as any).default?.RBACServiceClient;

      const rbacPbModule = await import('../../generated.commonjs/rbac_pb');
      const SetRbacYamlRequest = rbacPbModule.SetRbacYamlRequest || (rbacPbModule as any).default?.SetRbacYamlRequest;

      // Configure gRPC client with auth interceptor
      const authInterceptor = new AuthInterceptor(this.firebaseAuthService);
      const options = {
        unaryInterceptors: [authInterceptor],
      };

      const client = new RBACServiceClient(`https://${apiServer}:443`, null, options);

      // Create the request
      const request = new SetRbacYamlRequest();
      request.setYamlContent(yamlContent);

      ClientLoggingService.log('INFO', `[RbacService] Setting RBAC YAML configuration`);

      // Make the gRPC call
      const response = await client.setRbacYaml(request, null);
      const result = response.toObject();

      ClientLoggingService.log('INFO', `[RbacService] Set RBAC YAML result:`, result);
      return result;
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error setting RBAC YAML:', error);
      throw error;
    }
  }

  /**
   * Example method demonstrating progress tracking with cold start detection
   * This method wraps a gRPC call with progress tracking
   */
  getUserProjectsWithProgress(): Observable<any[]> {
    return this.grpcWrapper.wrapWithProgress(
      this.getUserProjects(),
      'Loading user projects...',
      'Projects loaded successfully'
    );
  }
}
